import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import {useSnackbar} from "../../../../../contexts/SnackbarContext";
import {adminConfigurationService} from "../../../../../services/admin-config.service";
import {
    ActiveDirectoryCode,
    ActiveDirectoryGroupWithRole,
    AD_GROUP_ROLE,
    AD_VALID_ROLES
} from "../../../../../models/admin-config.models";
import {Box, Button, Stack, Typography} from "@mui/material";
import {tokens} from "../../../../../theme";
import ChangeGroupRoleDialog from "../ChangeGroupRoleDialog";
import CircularProgress from "@mui/material/CircularProgress";

export function isValidRole(value: unknown): value is AD_GROUP_ROLE {
    return typeof value === "string" && (AD_VALID_ROLES as string[]).includes(value);
}

const ActiveDirectoryGroupsConfiguration = () => {
    const {t} = useTranslation();
    const { showSnackbar } = useSnackbar();

    const [isFetching, setIsFetching] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [adGroups, setAdGroups] = useState<ActiveDirectoryGroupWithRole[]>([]);
    const [adCode, setAdCode] = useState<ActiveDirectoryCode>({code: ''});

    const [openDialog, setOpenDialog] = useState(false);

    const loadConfig = useCallback(async () => {
        setIsFetching(true);

        try {
            const [code, groups, roles] = await Promise.all([
                adminConfigurationService.fetchActiveDirectoryCode(),
                adminConfigurationService.fetchActiveDirectoryGroups(),
                adminConfigurationService.fetchActiveDirectoryGroupRoles(),
            ]);

            const groupsWithRoles = groups.map(group => ({
                groupId: group.groupId,
                groupName: group.groupName,
                groupRole: isValidRole(roles.groupRoles[group.groupName as keyof typeof roles.groupRoles])
                    ? roles.groupRoles[group.groupName as keyof typeof roles.groupRoles]
                    : 'NOT_ASSIGNED',
            })) as ActiveDirectoryGroupWithRole[];

            setAdGroups(groupsWithRoles);
            setAdCode(code);

        } catch (e) {
            console.error(e);
            showSnackbar(t('configuration.loadError'), 'error');
        } finally {
            setIsFetching(false);
        }
    }, [t]);

    useEffect(() => {
        void loadConfig();
    }, []);

    const handleSave = async (groups: ActiveDirectoryGroupWithRole[]) => {
        if (!adCode || !adCode.code) {
            showSnackbar(t('configuration.adGroups.noCodeError'), 'error');
        }

        const assignments = groups.map(group => ({
            groupId: group.groupId,
            role: group.groupRole,
        }));

        setIsSaving(true);

        try {
            await adminConfigurationService.updateActiveDirectoryGroupRoles(adCode.code, assignments);
            await loadConfig();
            showSnackbar(t('configuration.adGroups.updateGroupRolesSuccess'), 'success');
        } catch (e) {
            console.error(e);
            showSnackbar(t('configuration.adGroups.updateGroupRolesError'), 'error');
        } finally {
            setOpenDialog(false);
            setIsSaving(false);
        }
    };

    return (
        <Stack direction='column' spacing={4}>
            {!adCode.code && (
                <Box display="flex" justifyContent="center" alignItems="center" textAlign="center">
                    <Typography variant="body1" color="error.main">
                        {t('configuration.adGroups.noCodeInfo')}
                    </Typography>
                </Box>
            )}

            {isFetching && (
                <Box display="flex" justifyContent="center" alignItems="center" textAlign="center" height="200px">
                    <CircularProgress size={36} />
                </Box>
            )}

            {!isFetching && adGroups.length === 0 && (
                <Box display="flex" justifyContent="center" alignItems="center" textAlign="center">
                    <Typography variant="body1" color="primary">
                        {t('configuration.adGroups.noGroupsDataInfo')}
                    </Typography>
                </Box>
            )}

            {!isFetching && adGroups.length > 0 &&
                (
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1} rowGap={3} pt={3} pb={3}>
                        <Typography gridColumn="1" variant="body2" textTransform="uppercase" color="secondary.light"
                                    fontWeight={tokens.fontWeight.medium}>
                            {t('configuration.adGroups.subtitleName')}
                        </Typography>
                        <Typography gridColumn="2" variant="body2" textTransform="uppercase" color="secondary.light"
                                    fontWeight={tokens.fontWeight.medium}>
                            {t('configuration.adGroups.subtitleRole')}
                        </Typography>

                        {adGroups.map(group => (
                            <React.Fragment key={group.groupId}>
                                <Typography gridColumn="1">
                                    {group.groupName}
                                </Typography>
                                <Typography gridColumn="2"
                                            color={group.groupRole === 'NOT_ASSIGNED' ? 'secondary.light' : 'text.primary'}>
                                    {group.groupRole === 'NOT_ASSIGNED' ? t('configuration.adGroups.notAssignedRole') : group.groupRole}
                                </Typography>
                            </React.Fragment>
                        ))}
                    </Box>
                )
            }

            <Box sx={{ alignSelf: 'flex-end' }}>
                <Button variant="outlined" onClick={() => setOpenDialog(true)} disabled={isFetching || isSaving || !adCode.code}>
                    {isSaving ? <CircularProgress size={18} /> : t('configuration.adGroups.changeSettingsBtn')}
                </Button>
            </Box>

            {openDialog && (
                <ChangeGroupRoleDialog
                    open={openDialog}
                    groups={adGroups}
                    onChange={handleSave}
                    onClose={() => setOpenDialog(false)}
                />
            )}
        </Stack>
    );
};

export default ActiveDirectoryGroupsConfiguration;